import React, { useEffect, useRef, useState } from "react";
import rough from "roughjs/bundled/rough.esm";
import styled from "styled-components";
import { useTheme } from "styled-components";

// Shared hand-drawn "chalk" primitives built on roughjs.
// Every box/underline/divider/circle in the site is drawn with these so the
// whole portfolio reads as one piece of chalk, not a pile of one-off SVGs.

const useElementSize = (ref) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        prev.width !== width || prev.height !== height
          ? { width, height }
          : prev
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
};

const Wrap = styled.div`
  position: relative;
  ${(p) => p.$inline && "display: inline-block;"}
`;

const RoughSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
`;

/** Hand-drawn wobbly rectangle behind children. Use like a card/border. */
export const RoughBox = React.forwardRef(function RoughBox(
  {
    children,
    stroke,
    fill,
    fillStyle = "hachure",
    strokeWidth = 1.75,
    roughness = 2.2,
    seed,
    bowing = 1.5,
    padding,
    className,
    style,
    inline = false,
    double = false,
  },
  fwdRef
) {
  const localRef = useRef(null);
  const ref = fwdRef || localRef;
  const svgRef = useRef(null);
  const theme = useTheme();
  const { width, height } = useElementSize(ref);
  const seedRef = useRef(seed ?? Math.floor(Math.random() * 2000) + 1);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || width < 4 || height < 4) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const rc = rough.svg(svg);
    const options = {
      stroke: stroke || theme.text_primary,
      strokeWidth,
      roughness,
      bowing,
      seed: seedRef.current,
      fill: fill || undefined,
      fillStyle,
    };
    const m = 4;
    svg.appendChild(
      rc.rectangle(m, m, Math.max(width - m * 2, 1), Math.max(height - m * 2, 1), options)
    );
    if (double) {
      svg.appendChild(
        rc.rectangle(m + 3, m + 3, Math.max(width - m * 2 - 6, 1), Math.max(height - m * 2 - 6, 1), {
          ...options,
          seed: seedRef.current + 1,
          strokeWidth: strokeWidth * 0.7,
        })
      );
    }
  }, [width, height, stroke, fill, fillStyle, strokeWidth, roughness, bowing, theme, double]);

  return (
    <Wrap ref={ref} className={className} style={style} $inline={inline}>
      <RoughSvg ref={svgRef} />
      <Content style={padding ? { padding } : undefined}>{children}</Content>
    </Wrap>
  );
});

/** Squiggly hand-drawn underline, sized to its wrapper's width. */
export const RoughUnderline = ({
  stroke,
  strokeWidth = 3,
  roughness = 1.8,
  height = 14,
  seed,
  style,
  className,
}) => {
  const ref = useRef(null);
  const svgRef = useRef(null);
  const theme = useTheme();
  const { width } = useElementSize(ref);
  const seedRef = useRef(seed ?? Math.floor(Math.random() * 2000) + 1);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || width < 4) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const rc = rough.svg(svg);
    svg.appendChild(
      rc.line(2, height / 2, width - 2, height / 2, {
        stroke: stroke || theme.primary,
        strokeWidth,
        roughness,
        bowing: 3,
        seed: seedRef.current,
      })
    );
  }, [width, height, stroke, strokeWidth, roughness, theme]);

  return (
    <div ref={ref} className={className} style={{ width: "100%", height, ...style }}>
      <svg ref={svgRef} width="100%" height={height} style={{ overflow: "visible", display: "block" }} />
    </div>
  );
};

/** Full-width wavy chalk line, used as a section divider. */
export const RoughDivider = ({ stroke, strokeWidth = 2, roughness = 1.5, height = 24, style }) => {
  const ref = useRef(null);
  const svgRef = useRef(null);
  const theme = useTheme();
  const { width } = useElementSize(ref);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || width < 4) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const rc = rough.svg(svg);
    const points = [];
    const segments = Math.max(Math.floor(width / 90), 3);
    for (let i = 0; i <= segments; i++) {
      const x = (width / segments) * i;
      const y = height / 2 + (i % 2 === 0 ? -1 : 1) * (height / 5);
      points.push([x, y]);
    }
    svg.appendChild(
      rc.curve(points, {
        stroke: stroke || theme.text_secondary,
        strokeWidth,
        roughness,
        bowing: 2,
      })
    );
  }, [width, height, stroke, strokeWidth, roughness, theme]);

  return (
    <div ref={ref} style={{ width: "100%", height, ...style }}>
      <svg ref={svgRef} width="100%" height={height} style={{ overflow: "visible", display: "block" }} />
    </div>
  );
};

/**
 * Small static squiggle as a CSS background-image (for repeated tiny UI like
 * nav links / tag pills, where mounting a full RoughUnderline per item would
 * be wasteful). Same hand-drawn look, cheaper for many instances.
 */
export const chalkSquiggleBg = (color, strokeWidth = 2.5) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='10'%3E%3Cpath d='M0 6 Q5 2 10 6 T20 6 T30 6 T40 6' stroke='${encodeURIComponent(
    color
  )}' stroke-width='${strokeWidth}' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`;

/** Hand-drawn circle, used as timeline dots / badges behind icons. */
export const RoughCircle = ({
  size = 44,
  stroke,
  fill,
  fillStyle = "hachure",
  strokeWidth = 1.75,
  roughness = 2,
  seed,
  style,
  className,
  children,
}) => {
  const svgRef = useRef(null);
  const theme = useTheme();
  const seedRef = useRef(seed ?? Math.floor(Math.random() * 2000) + 1);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const rc = rough.svg(svg);
    const m = 3;
    svg.appendChild(
      rc.circle(size / 2, size / 2, size - m * 2, {
        stroke: stroke || theme.text_primary,
        strokeWidth,
        roughness,
        seed: seedRef.current,
        fill: fill || undefined,
        fillStyle,
      })
    );
  }, [size, stroke, fill, fillStyle, strokeWidth, roughness, theme]);

  return (
    <div
      className={className}
      style={{ position: "relative", width: size, height: size, flexShrink: 0, ...style }}
    >
      <svg ref={svgRef} width={size} height={size} style={{ position: "absolute", inset: 0, overflow: "visible" }} />
      {children && (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
